import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Send, ShieldAlert } from 'lucide-react';
import type { BlacklistFeedbackIdentity, ForcedBlacklistFeedbackPreview } from '@uniticket/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { fetchForcedFeedbackPreview, forceBlacklistFeedback } from '@/hooks/useBlacklistFeedbackForce';

// « Forcer » (spec du user du 04-09, redemandée le 17-09) : Proximus demande pourquoi une adresse qu'ils ont
// blacklistée n'est pas visible chez eux — l'admin écrit dans le fichier de retour une ligne SANS modification
// d'adresse, avec un commentaire. La ligne EXACTE est montrée avant l'envoi, colonne par colonne : c'est un tiers
// qui la lira, et elle ne se reprend pas une fois écrite.
//
// Rendue par la PAGE, hors de la GlassCard du panneau (dont le backdrop-filter piégerait l'overlay fixe), et
// portalisée avec `pointer-events-auto` + z-[60] pour la même raison que DetailModal (défaut B-3).

const FIELDS: { key: keyof BlacklistFeedbackIdentity; column: string }[] = [
  { key: 'streetName', column: 'street_name' },
  { key: 'houseNumber', column: 'house_number' },
  { key: 'houseNumberExtension', column: 'house_number_extension' },
  { key: 'busNumber', column: 'bus_number' },
];

const fmt = (iso: string) => new Date(iso).toLocaleString();

export function ForceBlacklistFeedbackModal({
  jvid, addressLabel, onClose, onForced,
}: { jvid: number; addressLabel: string; onClose: () => void; onForced: () => void }) {
  const { t } = useTranslation(['tickets']);
  const k = (key: string, o?: Record<string, unknown>) => t(`tickets:dmpLookup.forceFeedback.${key}`, o);

  const [preview, setPreview] = useState<ForcedBlacklistFeedbackPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchForcedFeedbackPreview(jvid)
      .then((res) => {
        if (!alive) return;
        setPreview(res.data);
        setComment(res.data.defaultComment);
      })
      .catch((err: unknown) => { if (alive) setLoadError((err as Error)?.message || k('loadError')); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jvid]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const submit = async () => {
    if (!preview || preview.pending || !comment.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await forceBlacklistFeedback(jvid, comment.trim());
      toast.success(k(res.data.pushEnabled ? 'doneSoon' : 'doneWaiting'));
      onForced();
    } catch (err: unknown) {
      setError((err as Error)?.message || k('error'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={() => { if (!saving) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={k('title')}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl"
      >
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <ShieldAlert className="h-4 w-4 text-red-600" />
            {k('title')}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">JVID {jvid} · {addressLabel}</p>
        </div>

        <div className="space-y-3 px-5 py-4">
          <p className="text-xs text-slate-600">{k('explain')}</p>

          {!preview && !loadError && (
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {k('loading')}
            </p>
          )}
          {loadError && <p className="text-xs font-medium text-red-600" role="alert">{loadError}</p>}

          {preview && (
            <>
              {preview.blacklistReason && (
                <p className="text-[11px] text-slate-500">{k('reason', { reason: preview.blacklistReason })}</p>
              )}
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2 font-semibold">{k('colField')}</th>
                      <th className="px-3 py-2 font-semibold">previous_*</th>
                      <th className="px-3 py-2 font-semibold">new_*</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono text-slate-500">jvid</td>
                      <td colSpan={2} className="px-3 py-1.5 font-mono text-slate-800">{preview.jvid}</td>
                    </tr>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono text-slate-500">modification_date</td>
                      <td colSpan={2} className="px-3 py-1.5 font-mono text-slate-800">{preview.modificationDate}</td>
                    </tr>
                    {FIELDS.map((f) => {
                      const moved = preview.next[f.key] !== preview.previous[f.key];
                      return (
                        <tr key={f.key} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 font-mono text-slate-500">{f.column}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-800">{preview.previous[f.key] || '∅'}</td>
                          <td className={cn('px-3 py-1.5 font-mono', moved ? 'font-semibold text-emerald-700' : 'text-slate-800')}>
                            {preview.next[f.key] || '∅'}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-slate-100 align-top">
                      <td className="px-3 py-1.5 font-mono text-slate-500">
                        <label htmlFor="force-feedback-comment">comment</label>
                      </td>
                      <td colSpan={2} className="px-3 py-1.5">
                        <textarea
                          id="force-feedback-comment"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          maxLength={500}
                          rows={2}
                          className={cn(
                            'w-full rounded-lg border p-2 text-xs',
                            comment.trim() ? 'border-slate-200' : 'border-red-300 bg-red-50',
                          )}
                        />
                        {!comment.trim() && <p className="text-[10px] text-red-600">{k('commentRequired')}</p>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {preview.source === 'mapping' && <p className="text-[11px] text-slate-500">{k('fromMapping')}</p>}
              {preview.pending && (
                <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="alert">
                  {k('pending', { when: fmt(preview.pending.changedAt) })}
                </p>
              )}
              {!preview.pending && preview.lastSent && (
                <p className="text-[11px] font-medium text-amber-700">
                  {k('alreadySent', { when: fmt(preview.lastSent.syncedAt), row: preview.lastSent.pushedRow ?? '—' })}
                </p>
              )}
              {!preview.pushEnabled && <p className="text-[11px] font-medium text-amber-700">{k('pushOff')}</p>}
            </>
          )}

          {error && <p className="text-xs font-medium text-red-600" role="alert">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>{k('cancel')}</Button>
          <Button
            size="sm"
            onClick={() => void submit()}
            disabled={!preview || !!preview.pending || !comment.trim() || saving}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
            {k('submit')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
